import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, PlusCircle, Copy, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function fmtDollars(cents) {
    return ((cents || 0) / 100).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function AddAffiliateModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "", email: "", commission_rate_pct: 20, payout_handle: "", payout_method: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    async function submit() {
        setBusy(true); setErr(null);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                commission_rate: Math.max(0, Math.min(100, Number(form.commission_rate_pct) || 0)) / 100,
                payout_handle: form.payout_handle || null,
                payout_method: form.payout_method || null,
            };
            const r = await api.adminCreateAffiliate(payload);
            setResult(r);
            onCreated?.();
        } catch (e) { setErr(e.message); }
        finally { setBusy(false); }
    }

    async function copy() {
        try {
            await navigator.clipboard.writeText(result.set_password_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch { /* noop */ }
    }

    function reset() {
        setForm({ name: "", email: "", commission_rate_pct: 20, payout_handle: "", payout_method: "" });
        setResult(null); setErr(null);
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && (reset(), onClose())}>
            <DialogContent className="sm:max-w-lg" data-testid="add-affiliate-dialog">
                <DialogHeader>
                    <DialogTitle>Add affiliate manually</DialogTitle>
                    <DialogDescription>
                        Creates a pre-approved affiliate account. A one-time set-password link is generated (24 h expiry) which you share with the affiliate manually.
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <div className="space-y-3">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className="st-label">Full name</label>
                                <input className="st-input" value={form.name}
                                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                                       data-testid="add-aff-name" />
                            </div>
                            <div>
                                <label className="st-label">Commission rate (%)</label>
                                <input type="number" min="0" max="100" step="1" className="st-input"
                                       value={form.commission_rate_pct}
                                       onChange={(e) => setForm({ ...form, commission_rate_pct: e.target.value })}
                                       data-testid="add-aff-rate" />
                            </div>
                        </div>
                        <div>
                            <label className="st-label">Email</label>
                            <input type="email" className="st-input" value={form.email}
                                   onChange={(e) => setForm({ ...form, email: e.target.value })}
                                   data-testid="add-aff-email" />
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className="st-label">Payout method (optional)</label>
                                <select className="st-input" value={form.payout_method}
                                        onChange={(e) => setForm({ ...form, payout_method: e.target.value })}
                                        data-testid="add-aff-payout-method">
                                    <option value="">Select…</option>
                                    <option value="paypal">PayPal</option>
                                    <option value="zelle">Zelle</option>
                                    <option value="cashapp">Cash App</option>
                                    <option value="venmo">Venmo</option>
                                </select>
                            </div>
                            <div>
                                <label className="st-label">Payout handle (optional)</label>
                                <input className="st-input" value={form.payout_handle}
                                       onChange={(e) => setForm({ ...form, payout_handle: e.target.value })}
                                       placeholder="email / phone / $cashtag / @venmo"
                                       data-testid="add-aff-payout-handle" />
                            </div>
                        </div>
                        {err && <div className="text-sm p-2 rounded" style={{ background: "#FEF3F2", color: "#B42318" }} data-testid="add-aff-error">{err}</div>}
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="add-aff-success">
                        <div className="rounded p-3 text-sm" style={{ background: "#ECFDF3", color: "#067647", border: "1px solid #ABEFC6" }}>
                            Affiliate created. Ref code: <strong>{result.ref_code}</strong>.
                        </div>
                        <div>
                            <label className="st-label">One-time set-password link (expires in {result.set_password_expires_hours}h)</label>
                            <div className="flex gap-2">
                                <div className="flex-1 px-3 py-2 rounded font-mono text-xs break-all"
                                     style={{ background: "var(--st-silver-50)", border: "1px solid var(--st-line)" }}
                                     data-testid="set-password-url">
                                    {result.set_password_url}
                                </div>
                                <button type="button" className="btn-royal text-sm inline-flex items-center gap-2" onClick={copy}
                                        data-testid="copy-set-password-url">
                                    {copied ? <><CheckCircle2 size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                                </button>
                            </div>
                            <p className="text-xs mt-2" style={{ color: "var(--st-silver-500)" }}>
                                Send this link to the affiliate. It expires in 24 hours. Phase 6 will send this automatically via email.
                            </p>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    {!result ? (
                        <>
                            <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
                            <button type="button" className="btn-royal text-sm"
                                    disabled={busy || !form.name.trim() || !form.email.includes("@")}
                                    onClick={submit}
                                    data-testid="add-aff-submit">
                                {busy ? "Creating…" : "Create affiliate"}
                            </button>
                        </>
                    ) : (
                        <button type="button" className="btn-royal text-sm" onClick={() => { reset(); onClose(); }}
                                data-testid="add-aff-done">
                            Done
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const TABS = [
    { key: "",     label: "All" },
    { key: "true", label: "Approved & active" },
    { key: "false",label: "Pending" },
];

export default function AdminAffiliates() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [approved, setApproved] = useState("");
    const [deactivated, setDeactivated] = useState("");
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const params = { q: q || undefined };
            if (deactivated === "true") params.deactivated = "true";
            else {
                params.deactivated = "false";
                if (approved) params.approved = approved;
            }
            const r = await api.adminListAffiliates(params);
            setRows(r.affiliates);
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [approved, deactivated]);

    return (
        <section className="max-w-6xl mx-auto px-6 py-10 space-y-6" data-testid="admin-affiliates-page">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="tracking-caps text-[11px] font-semibold" style={{ color: "var(--st-royal)" }}>Affiliates</div>
                    <h1 className="font-serif text-4xl mt-2" style={{ color: "var(--st-navy)" }}>Manage affiliates</h1>
                    <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                        Approve applicants, set commission rates, add or deactivate affiliates, and review referred conversions.
                    </p>
                </div>
                <button type="button" className="btn-royal text-sm inline-flex items-center gap-2"
                        onClick={() => setAddOpen(true)}
                        data-testid="add-affiliate-btn">
                    <PlusCircle size={14} /> Add affiliate
                </button>
            </div>

            <div className="flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--st-line)" }} data-testid="affiliates-tabs">
                {TABS.map((t) => (
                    <button key={t.key} type="button"
                            onClick={() => { setDeactivated(""); setApproved(t.key); }}
                            className="pb-2 px-3 text-sm font-semibold"
                            style={{
                                color: (deactivated !== "true" && approved === t.key) ? "var(--st-navy)" : "var(--st-ink-muted)",
                                borderBottom: (deactivated !== "true" && approved === t.key) ? "2px solid var(--st-royal)" : "2px solid transparent",
                            }}
                            data-testid={`affiliates-tab-${t.label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                        {t.label}
                    </button>
                ))}
                <button type="button"
                        onClick={() => { setApproved(""); setDeactivated("true"); }}
                        className="pb-2 px-3 text-sm font-semibold"
                        style={{
                            color: deactivated === "true" ? "var(--st-navy)" : "var(--st-ink-muted)",
                            borderBottom: deactivated === "true" ? "2px solid var(--st-royal)" : "2px solid transparent",
                        }}
                        data-testid="affiliates-tab-deactivated">
                    Deactivated
                </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2" data-testid="admin-affiliates-search">
                <div className="flex-1 relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" color="var(--st-silver-500)" />
                    <input className="st-input" style={{ paddingLeft: "2rem" }} value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder="Search name, email, or ref code"
                           data-testid="affiliates-search-input" />
                </div>
                <button type="submit" className="btn-royal text-sm" data-testid="affiliates-apply-filter">Search</button>
            </form>

            <div className="st-surface overflow-hidden" data-testid="admin-affiliates-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Affiliate</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Ref code</th>
                            <th className="text-left px-4 py-3 tracking-caps text-[10px] font-semibold">Payout</th>
                            <th className="text-center px-4 py-3 tracking-caps text-[10px] font-semibold">Status</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Rate</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Clicks</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Convs</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Earned</th>
                            <th className="text-right px-4 py-3 tracking-caps text-[10px] font-semibold">Pending</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={10} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>}
                        {!loading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }}>No affiliates.</td></tr>}
                        {rows.map((r) => {
                            const isDeactivated = !!r.deactivated_at;
                            const status = isDeactivated ? "DEACTIVATED" : (r.approved ? "APPROVED" : "PENDING");
                            const bg = isDeactivated ? "#F2F4F7" : (r.approved ? "#ECFDF3" : "#FFFAEB");
                            const fg = isDeactivated ? "#475467" : (r.approved ? "#067647" : "#B54708");
                            return (
                                <tr key={r.id} style={{ borderTop: "1px solid var(--st-line)" }} data-testid={`affiliate-row-${r.id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold" style={{ color: "var(--st-navy)" }}>{r.user.name || "—"}</div>
                                        <div className="text-xs" style={{ color: "var(--st-silver-500)" }}>{r.user.email}</div>
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs">{r.ref_code}</td>
                                    <td className="px-4 py-3 text-xs" data-testid={`aff-payout-cell-${r.id}`}>
                                        {r.payout_method && r.payout_handle ? (
                                            <span>
                                                <span className="tracking-caps font-semibold" style={{ color: "var(--st-navy)" }}>{r.payout_method}</span>
                                                <span style={{ color: "var(--st-silver-500)" }}> · </span>
                                                <span className="truncate inline-block max-w-[200px] align-middle" title={r.payout_handle} style={{ color: "var(--st-ink-muted)" }}>{r.payout_handle}</span>
                                            </span>
                                        ) : (
                                            <span style={{ color: "#B54708" }}>— not set</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <span className="px-2 py-0.5 rounded-full text-[10px] tracking-caps font-bold"
                                              style={{ background: bg, color: fg }}
                                              data-testid={`affiliate-status-${r.id}`}>
                                            {status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">{Math.round(r.commission_rate * 100)}%</td>
                                    <td className="px-4 py-3 text-right">{r.stats_lite.clicks}</td>
                                    <td className="px-4 py-3 text-right">{r.stats_lite.conversions}</td>
                                    <td className="px-4 py-3 text-right">{fmtDollars(r.stats_lite.earned_cents)}</td>
                                    <td className="px-4 py-3 text-right font-semibold" style={{ color: "var(--st-navy)" }}>{fmtDollars(r.stats_lite.pending_cents)}</td>
                                    <td className="px-4 py-3 text-right">
                                        <Link to={`/admin/affiliates/${r.id}`} className="link-royal text-sm" data-testid={`open-affiliate-${r.id}`}>
                                            Open →
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <AddAffiliateModal open={addOpen} onClose={() => setAddOpen(false)} onCreated={load} />
        </section>
    );
}
